import React, { useCallback, useContext, useEffect, useState } from "react";
import _ from "lodash";
import eventEmitter from "../../shared/events";
import * as notify from "../../shared/notify";

import { TaskType } from "shared/taskTypeMap";
import AttributeList from "../attributes/attribute-list";
import AttributeErrors from "../attributes/attribute-errors";
import CompareHeader from "./compare-table/compare-header";
import Loader from "../loader/loader";
import SelectReferences from "./references/selectReferences";

import { TaskContext } from "../../contexts/task";
import { attributesByGroup, getAttributesForReferences, getAttributesGroup, getAttributesGroupByTaskKey } from "./lib/attribute-data";
import { addReferences, deleteReference, getReferences, getSampleReferences } from "./lib/referenceData";
import { sortByOrder } from "./lib/helpers";
import { useBuildSubgroups } from "./hooks/use-build-subgroups";
import { useFlattenSubGroups } from "../../shared/hooks/use-flatten-sub-groups";

const AttributeTable = ({
    readOnlyOverride,
    showValidation,
    showReferences,
    onHideControls,
    taskType,
    isQa,
    isReadOnlyAdd,
    isReadOnlyEdit,
    isReadOnlyDelete,
}) => {
    const flattenSubGroups = useFlattenSubGroups();
    const [columnWidths, setColumnWidth] = useState({});
    const [modalSelect, setModalSelect] = useState(false);
    const buildSubGroups = useBuildSubgroups();
    const { task } = useContext(TaskContext);
    const codingWipKey = task.context.codingWipKey;
    const [definitions, setDefinitions] = useState({
        isLoaded: false,
        byGroup: {},
        subGroups: {},
        groups: [],
    });
    const [references, setReferences] = useState({
        referencesLoaded: false,
        activeProducts: [],
        activeProductKeys: [],
        products: [],
    });

    useEffect(() => {
        if (references.referencesLoaded && references.activeProducts.length === 0) {
            fetchScaffold();
        }
    }, [references.referencesLoaded]);

    useEffect(() => {
        // TODO: refactor this to use hooks
        fetchReferences();

        eventEmitter.on("ADD_ATTRIBUTE", fetchScaffold);
        eventEmitter.on("REFRESH_ATTRIBUTE_DEFINITIONS", fetchScaffold);

        return () => {
            eventEmitter.removeListener("ADD_ATTRIBUTE", fetchScaffold);
            eventEmitter.removeListener("REFRESH_ATTRIBUTE_DEFINITIONS", fetchScaffold);
        };
    }, []);

    useEffect(() => {
        // TODO: this is called twice on init when refs are available, adjust
        fetchScaffold();
    }, [references, references.activeProductKeys]);

    const fetchReferences = async () => {
        const refs = await getReferences(codingWipKey);
        const referenceProducts = refs.sort(sortByOrder);

        if (!referenceProducts || referenceProducts.length < 1) {
            const topReferences = await getSampleReferences(codingWipKey, task.upc);

            if (topReferences && topReferences.length > 0) {
                updateReferenceData(topReferences);
            }
        } else {
            const activeProducts = referenceProducts.filter((ref) => ref.order > 0);
            const activeProductKeys = activeProducts.map((r) => r.key);
            setReferences({
                referencesLoaded: true,
                activeProducts,
                activeProductKeys,
                products: referenceProducts,
            });
        }
    };

    const updateReferenceData = async (products) => {
        const refs = products.map((ref) => {
            return { pid: ref.key, _seq: ref.order.toString() === "0" ? null : ref.order };
        });
        const activeProducts = products.filter((ref) => ref.order > 0).sort(sortByOrder);
        const activeProductKeys = activeProducts.map((ref) => ref.key);

        const saveReferences = await addReferences(codingWipKey, { refs: refs });

        if (!saveReferences) {
            return;
        }

        setReferences({
            activeProducts,
            activeProductKeys,
            products: products,
        });

        notify.success("References saved");

        setModalSelect(false);
        onHideControls && onHideControls(false);
    };

    const fetchScaffold = async () => {
        if (taskType === TaskType.FINAL_QA) {
            const fqaAttributes = await getAttributesGroupByTaskKey("FqaAttributes", task.key);

            const fqaGroups = fqaAttributes.subGroups;

            const attributesGroupSort = () => {
                const groups = [];
                const byGroup = fqaGroups.reduce((obj, a) => {
                    const groupName = a.groupName;
                    const attributes = a.attributes;
                    const sortGroup = { name: groupName, label: a.groupLabel, sortOrder: a.sortOrder };

                    if (!obj[groupName]) {
                        obj[groupName] = [];
                        groups.push(sortGroup);
                    }

                    obj[groupName].push(...attributes);

                    return obj;
                }, {});

                const subGroups = fqaGroups.reduce((obj, a) => {
                    const groupName = a.groupName;
                    const attributeSubGroups = a.subGroups;

                    if (!obj[groupName] && !_.isEmpty(attributeSubGroups)) {
                        obj[groupName] = [];
                    }

                    obj[groupName] && obj[groupName].push(...attributeSubGroups);

                    return obj;
                }, {});

                return {
                    groups,
                    byGroup,
                    subGroups,
                };
            };

            const fqaGroupsSorted = attributesGroupSort();

            return setDefinitions({
                isLoaded: true,
                byGroup: fqaGroupsSorted.byGroup,
                groups: fqaGroupsSorted.groups,
                subGroups: fqaGroupsSorted.subGroups,
            });
        }

        const attrDefinitions = await getAttributesForReferences({
            wip: codingWipKey,
            pids: [...references.activeProductKeys],
            taskType: taskType,
        });
        const codingSizeGroup = await getAttributesGroup("IntakeCodingSizeAttributes", codingWipKey);

        // filter 'IntakeCodingSizeAttributes' and 'IntakeCodingIdentifyingGroup' which are currently set up by sub grouping
        const filteredAttributes = attrDefinitions.filter(
            ({ sortGroup }) => sortGroup && sortGroup.name !== "IntakeCodingSizeAttributes" && sortGroup.name !== "IntakeCodingIdentifyingGroup"
        );
        // merge 'coding' and other attributes then filter out duplicates
        const newAttributes = [...codingSizeGroup.attributes, ...filteredAttributes].filter(
            ({ uuid }, index, allAttributes) => index === allAttributes.findIndex((t) => t.uuid === uuid)
        );
        // TODO: revisit - this is not implemented correctly, it's creating attributes and subgroups which is causing issues on Coding/Coding QA
        const subGroups = buildSubGroups(codingSizeGroup.subGroups);
        // extract subgroups and flatten the tree
        const subGroupAttributes = flattenSubGroups(codingSizeGroup.subGroups);
        // filter out duplicated uuids
        const uniqueAttributes = newAttributes.filter((attribute) => {
            return !subGroupAttributes.some((sub) => sub.uuid === attribute.uuid);
        });
        const buildByGroup = attributesByGroup(uniqueAttributes);

        setDefinitions({
            isLoaded: true,
            byGroup: buildByGroup.byGroup,
            groups: buildByGroup.groups,
            subGroups: {
                IntakeCodingSizeAttributes: subGroups,
            },
        });
    };

    const toggleModalSelect = () => {
        setModalSelect(!modalSelect);
        onHideControls && onHideControls(!modalSelect);
    };

    const handleColumnWidthChange = useCallback((col, width) => {
        setColumnWidth((state) => ({ ...state, [col]: width }));
    }, []);

    const handleInitialColSizes = useCallback(() => setColumnWidth({}), []);

    const selectReferences = modalSelect ? (
        <div>
            <SelectReferences
                updateReferences={updateReferenceData}
                references={references.products}
                deleteReference={deleteReference}
                wipKey={codingWipKey}
            />
        </div>
    ) : null;

    const selectStyles = modalSelect ? { display: "none" } : {};

    if (!definitions.isLoaded) {
        return (
            <div className="txt-center">
                <Loader natural={true} />
            </div>
        );
    }

    return (
        <>
            <div className="attribute-table">
                {selectReferences}
                <div style={selectStyles}>
                    <CompareHeader
                        isQa={isQa}
                        columnWidths={columnWidths}
                        referenceProducts={showReferences ? references.activeProducts : []}
                        toggleModalSelect={toggleModalSelect}
                        hideSelect={!showReferences}
                        onColumnWidthChange={handleColumnWidthChange}
                        onInitColSizes={handleInitialColSizes}
                    />
                    {!_.isEmpty(definitions.byGroup) && (
                        <AttributeErrors
                            attributeGroups={definitions.groups}
                            attributesByGroup={definitions.byGroup}
                            attributesSubgroups={definitions.subGroups}
                            readOnlyOverride={readOnlyOverride}
                            showValidation={showValidation}
                        />
                    )}
                    <AttributeList
                        columnWidths={columnWidths}
                        isCompleted={task.isCompleted}
                        attributeGroups={definitions.groups}
                        attributesByGroup={definitions.byGroup}
                        attributesSubgroups={definitions.subGroups}
                        references={showReferences ? references.activeProducts : []}
                        referenceKeys={references.activeProductKeys}
                        removeAttribute={fetchScaffold}
                        readOnlyOverride={readOnlyOverride}
                        taskType={taskType}
                        isReadOnlyAdd={isReadOnlyAdd}
                        isReadOnlyEdit={isReadOnlyEdit}
                        isReadOnlyDelete={isReadOnlyDelete}
                    />
                </div>
            </div>
        </>
    );
};

AttributeTable.defaultProps = {
    fetchDefinitions: null,
    showValidation: true,
    hideReferences: false,
};

export default AttributeTable;
